<template>
  <div class="bg-white rounded-lg shadow-sm border">
    <!-- Category Header -->
    <div class="p-4 border-b bg-gray-50 rounded-t-lg">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <h3 class="text-lg font-medium text-gray-900" @click="editCategoryName">
            {{ category.name }}
          </h3>
          <span class="text-sm text-gray-500">
            ({{ completedCount }}/{{ category.items.length }})
          </span>
        </div>
        <div class="flex items-center space-x-2">
          <button 
            @click="copyToClipboard"
            class="text-green-600 hover:text-green-800 transition-colors"
            title="Copy category to clipboard (YAML)"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
            </svg>
          </button>
          <button 
            @click="pasteFromClipboard"
            class="text-purple-600 hover:text-purple-800 transition-colors"
            title="Paste items from clipboard (YAML)"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </button>
          <button 
            @click="openAddItemDialog"
            class="text-blue-600 hover:text-blue-800 transition-colors"
            title="Add item"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <button 
            @click="confirmDelete"
            class="text-red-600 hover:text-red-800 transition-colors"
            title="Delete category"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Items List -->
    <div class="p-4">
      <draggable
        v-model="localItems"
        group="items"
        item-key="id"
        handle=".drag-handle"
        class="space-y-2 min-h-[60px]"
        :data-category-id="category.id"
        @start="onDragStart"
        @end="onDragEnd"
        ghost-class="opacity-50"
        chosen-class="ring-2"
        animation="150"
      >
        <template #item="{ element }">
          <ItemComponent
            :key="element.id"
            :item="element"
            @update-item="$emit('update-item', $event.id, $event.data)"
            @delete-item="$emit('delete-item', $event)"
          />
        </template>
        <template v-if="category.items.length === 0" #footer>
          <div class="text-gray-500 text-center py-8">
            No items yet. Click the + button to add your first item, or drag items here.
          </div>
        </template>
      </draggable>
    </div>

    <!-- Add Item Dialog -->
    <dialog 
      ref="addItemDialog" 
      class="backdrop:bg-black backdrop:bg-opacity-50 p-0 rounded-lg shadow-xl max-w-md w-full border-0"
      @click="handleDialogBackdropClick"
    >
      <div class="p-6 bg-white rounded-lg">
        <h3 class="text-lg font-semibold mb-4">Add New Item</h3>
        <form @submit.prevent="handleCreateItem">
          <input
            ref="itemNameInput"
            v-model="newItemName"
            type="text"
            placeholder="Item name"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            required
            @keydown.escape="closeAddItemDialog"
          >
          <input
            v-model="newItemQuantity"
            type="number"
            placeholder="Quantity (optional)"
            min="1"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            @keydown.escape="closeAddItemDialog"
          >
          <div class="flex justify-end space-x-2">
            <button 
              type="button"
              @click="closeAddItemDialog"
              class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Add Item
            </button>
          </div>
        </form>
      </div>
    </dialog>

    <!-- Edit Category Name Dialog -->
    <dialog 
      ref="editCategoryDialog" 
      class="backdrop:bg-black backdrop:bg-opacity-50 p-0 rounded-lg shadow-xl max-w-md w-full border-0"
      @click="handleDialogBackdropClick"
    >
      <div class="p-6 bg-white rounded-lg">
        <h3 class="text-lg font-semibold mb-4">Edit Category Name</h3>
        <form @submit.prevent="handleUpdateCategory">
          <input
            ref="categoryNameInput"
            v-model="editCategoryValue"
            type="text"
            placeholder="Category name"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            required
            @keydown.escape="closeEditCategoryDialog"
          >
          <div class="flex justify-end space-x-2">
            <button 
              type="button"
              @click="closeEditCategoryDialog"
              class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </dialog>

    <!-- Confirm Delete Dialog -->
    <dialog 
      ref="confirmDeleteDialog" 
      class="backdrop:bg-black backdrop:bg-opacity-50 p-0 rounded-lg shadow-xl max-w-md w-full border-0"
      @click="handleDialogBackdropClick"
    >
      <div class="p-6 bg-white rounded-lg">
        <h3 class="text-lg font-semibold mb-4 text-red-600">Delete Category</h3>
        <p class="text-gray-600 mb-6">
          Are you sure you want to delete "{{ category.name }}" and all its items? This action cannot be undone.
        </p>
        <div class="flex justify-end space-x-2">
          <button 
            @click="closeConfirmDeleteDialog"
            class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button 
            @click="handleDeleteCategory"
            class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { Category, Item } from '@/types'
import ItemComponent from '@/components/ItemComponent.vue'
import draggable from 'vuedraggable'
import * as yaml from 'js-yaml'

const props = defineProps<{
  category: Category & { items: Item[] }
}>()

const emit = defineEmits<{
  'update-category': [id: string, data: Partial<Category>]
  'delete-category': [id: string]
  'create-item': [categoryId: string, name: string, quantity?: number]
  'update-item': [id: string, data: Partial<Item>]
  'delete-item': [id: string]
  'reorder-items': [categoryId: string, newOrder: string[]]
  'move-item': [itemId: string, newCategoryId: string, newOrder: number]
}>()

// Dialog refs
const addItemDialog = ref<HTMLDialogElement>()
const editCategoryDialog = ref<HTMLDialogElement>()
const confirmDeleteDialog = ref<HTMLDialogElement>()
const itemNameInput = ref<HTMLInputElement>()
const categoryNameInput = ref<HTMLInputElement>()

// Form data
const newItemName = ref('')
const newItemQuantity = ref<number | undefined>()
const editCategoryValue = ref('')

// Computed
const completedCount = computed(() => {
  return props.category.items.filter(item => item.completed).length
})

const sortedItems = computed(() => props.category.items.sort((a, b) => a.order - b.order))

const localItems = ref<typeof sortedItems.value>([])
const isDragging = ref(false)

// Watch for changes in sortedItems and update localItems
watch(sortedItems, (newItems) => {
  if (!isDragging.value) {
    localItems.value = [...newItems]
  }
}, { immediate: true, deep: true })

// Methods
const editCategoryName = () => {
  editCategoryValue.value = props.category.name
  editCategoryDialog.value?.showModal()
  setTimeout(() => {
    categoryNameInput.value?.focus()
    categoryNameInput.value?.select()
  }, 100)
}

const confirmDelete = () => {
  confirmDeleteDialog.value?.showModal()
}

const openAddItemDialog = () => {
  addItemDialog.value?.showModal()
  setTimeout(() => {
    itemNameInput.value?.focus()
  }, 100)
}

const handleCreateItem = () => {
  if (newItemName.value.trim()) {
    emit('create-item', props.category.id, newItemName.value.trim(), newItemQuantity.value)
    closeAddItemDialog()
  }
}

const handleUpdateCategory = () => {
  if (editCategoryValue.value.trim()) {
    emit('update-category', props.category.id, { name: editCategoryValue.value.trim() })
    closeEditCategoryDialog()
  }
}

const handleDeleteCategory = () => {
  emit('delete-category', props.category.id)
  closeConfirmDeleteDialog()
}

const closeAddItemDialog = () => {
  addItemDialog.value?.close()
  newItemName.value = ''
  newItemQuantity.value = undefined
}

const closeEditCategoryDialog = () => {
  editCategoryDialog.value?.close()
  editCategoryValue.value = ''
}

const closeConfirmDeleteDialog = () => {
  confirmDeleteDialog.value?.close()
}

const onDragStart = () => {
  isDragging.value = true
}

const onDragEnd = async (event: any) => {
  console.log('Drag end:', event)
  console.log('Local items after drag:', localItems.value.map(i => ({ id: i.id, name: i.name })))
  
  const { from, to, oldIndex, newIndex } = event
  
  if (from === to) {
    // Item moved within the same category
    if (oldIndex !== newIndex) {
      // Get the new order from localItems (which vuedraggable has already updated)
      const newOrder = localItems.value.map(item => item.id)
      console.log('Emitting reorder-items:', props.category.id, newOrder)
      emit('reorder-items', props.category.id, newOrder)
      
      // Wait a bit for the store to update, then allow watcher to sync again
      setTimeout(() => {
        isDragging.value = false
      }, 1000)
    } else {
      isDragging.value = false
    }
  } else {
    // Item moved to different category
    const itemId = event.item.getAttribute('data-item-id')
    const targetCategoryId = to.getAttribute('data-category-id')
    
    if (itemId && targetCategoryId && targetCategoryId !== props.category.id) {
      console.log('Emitting move-item:', itemId, 'to category:', targetCategoryId, 'at index:', newIndex)
      emit('move-item', itemId, targetCategoryId, newIndex)
    }
    
    setTimeout(() => {
      isDragging.value = false
    }, 1000)
  }
}

// Handle dialog backdrop clicks (close on click outside)
const handleDialogBackdropClick = (event: MouseEvent) => {
  const dialog = event.target as HTMLDialogElement
  if (event.target === dialog && dialog && typeof dialog.close === 'function') {
    dialog.close()
    // Clean up form data
    if (dialog === addItemDialog.value) {
      newItemName.value = ''
      newItemQuantity.value = undefined
    } else if (dialog === editCategoryDialog.value) {
      editCategoryValue.value = ''
    }
  }
}

// Clipboard functionality
const copyToClipboard = async () => {
  try {
    // Create YAML data for the category
    const categoryData = {
      name: props.category.name,
      items: props.category.items.map(item => ({
        name: item.name,
        completed: item.completed,
        quantity: item.quantity || 1,
        notes: item.notes || '',
        order: item.order
      })).sort((a, b) => a.order - b.order)
    }

    const yamlString = yaml.dump(categoryData, {
      indent: 2,
      lineWidth: -1,
      noRefs: true
    })

    await navigator.clipboard.writeText(yamlString)
    
    // Show feedback
    const button = event?.target as HTMLButtonElement
    const originalTitle = button?.title
    if (button) {
      button.title = 'Copied!'
      setTimeout(() => {
        button.title = originalTitle || 'Copy category to clipboard (YAML)'
      }, 2000)
    }
  } catch (err) {
    console.error('Failed to copy to clipboard:', err)
    alert('Failed to copy to clipboard. Please try again.')
  }
}

const pasteFromClipboard = async () => {
  try {
    const clipboardText = await navigator.clipboard.readText()
    
    if (!clipboardText.trim()) {
      alert('Clipboard is empty')
      return
    }

    // Parse YAML data
    const parsedData = yaml.load(clipboardText) as any
    
    if (!parsedData || !parsedData.items || !Array.isArray(parsedData.items)) {
      alert('Invalid YAML format. Expected a category with items.')
      return
    }

    // Add each item to the current category
    let addedCount = 0
    for (const itemData of parsedData.items) {
      if (itemData.name && typeof itemData.name === 'string') {
        emit('create-item', props.category.id, itemData.name, itemData.quantity || 1)
        addedCount++
        // Add small delay between items to prevent rate limiting
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    }

    if (addedCount > 0) {
      // Show feedback
      const button = event?.target as HTMLButtonElement
      const originalTitle = button?.title
      if (button) {
        button.title = `Pasted ${addedCount} items!`
        setTimeout(() => {
          button.title = originalTitle || 'Paste items from clipboard (YAML)'
        }, 3000)
      }
    } else {
      alert('No valid items found in clipboard data')
    }

  } catch (err) {
    console.error('Failed to paste from clipboard:', err)
    if (err instanceof yaml.YAMLException) {
      alert('Invalid YAML format in clipboard. Please copy a valid category first.')
    } else {
      alert('Failed to paste from clipboard. Please try again.')
    }
  }
}

</script>